/**
 * PURAVIDA UNIVERSAL SHOWCASE - CORE LOGIC
 * Handles interactions, animations, and performance optimizations.
 * Loaded once, instantiated per section.
 */

(function () {
    if (window.PuravidaShowcase) return;

    // Breakpoint unificado: debe coincidir con min-width: 991px del CSS
    const DESKTOP_BP = 991;

    class PuravidaShowcase {
        constructor(sectionId) {
            // Destruir instancia previa si existe (re-render en Theme Editor)
            const existing = PuravidaShowcaseManager.instances.find(i => i.sectionId === sectionId);
            if (existing) existing.destroy();

            this.sectionId = sectionId;
            this.container = document.getElementById(`shopify-section-${sectionId}`);
            this.grid = document.getElementById(`ShowcaseGrid-${sectionId}`);

            if (!this.grid) return;

            this.cards = this.grid.querySelectorAll('.showcase-card-wrapper');
            this.dots = this.container.querySelectorAll('.indicator-dot');
            this.hint = document.getElementById(`SwipeHint-${sectionId}`);

            this.isVisible = false;
            this.visibleCards = new Set();
            this.currentActiveIndex = 0;
            this.observers = [];
            this._rafId = null;

            // Handlers de Theme Editor — almacenados para poder limpiarlos en destroy()
            this._unloadHandler = (e) => {
                if (e.detail.sectionId === this.sectionId) this.destroy();
            };
            this._blockSelectHandler = (e) => {
                if (e.detail.sectionId !== this.sectionId) return;
                const blockEl = this.grid.querySelector(`[data-block-id="${e.detail.blockId}"]`);
                if (blockEl) blockEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            };

            this.init();
        }

        init() {
            this.setupImageReveal();
            this.setupMobileLayout();

            if (window.innerWidth >= DESKTOP_BP) {
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(() => this.setupDesktopEffects(), { timeout: 1500 });
                } else {
                    requestAnimationFrame(() => this.setupDesktopEffects());
                }
            }

            this.setupVideoOptimization();
            this.setupVisibilityTracking();
            this.setupDotNavigation();

            // Theme Editor lifecycle — solo activo en modo diseño
            if (typeof Shopify !== 'undefined' && Shopify.designMode) {
                document.addEventListener('shopify:section:unload', this._unloadHandler);
                document.addEventListener('shopify:section:block:select', this._blockSelectHandler);
            }

            PuravidaShowcaseManager.register(this);
        }

        // ── Image Reveal ──────────────────────────────────────────────

        setupImageReveal() {
            const elements = this.grid.querySelectorAll('.showcase-product-img.img-loading');
            elements.forEach(el => {
                const reveal = () => {
                    el.classList.remove('img-loading');
                    el.classList.add('img-revealed');
                };
                if (el.tagName === 'IMG') {
                    if (el.complete && el.naturalHeight > 0) {
                        requestAnimationFrame(() => requestAnimationFrame(reveal));
                    } else {
                        el.addEventListener('load', reveal, { once: true });
                        el.addEventListener('error', reveal, { once: true });
                    }
                } else {
                    reveal();
                }
            });
        }

        // ── Visibility Tracking ───────────────────────────────────────

        setupVisibilityTracking() {
            if (!('IntersectionObserver' in window)) {
                this.isVisible = true;
                this.cards.forEach(card => this.visibleCards.add(card.dataset.index));
                return;
            }

            const sectionObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    this.isVisible = entry.isIntersecting;
                    PuravidaShowcaseManager.checkActiveInstances();
                });
            }, { threshold: 0.05 });
            sectionObserver.observe(this.container);
            this.observers.push(sectionObserver);

            const cardObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const index = entry.target.dataset.index;
                    if (entry.isIntersecting) {
                        this.visibleCards.add(index);
                    } else {
                        this.visibleCards.delete(index);
                    }
                });
            }, { threshold: 0.1 });
            this.cards.forEach(card => cardObserver.observe(card));
            this.observers.push(cardObserver);
        }

        // ── Video Optimization ────────────────────────────────────────

        setupVideoOptimization() {
            const bgVideos = this.grid.querySelectorAll('video.lazy-video');
            if (bgVideos.length > 0 && 'IntersectionObserver' in window) {
                const videoObserver = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            entry.target.play().catch(() => {});
                        } else {
                            entry.target.pause();
                        }
                    });
                }, { threshold: 0.1 });
                bgVideos.forEach(video => videoObserver.observe(video));
                this.observers.push(videoObserver);
            }

            // Hover videos — delegación de eventos, más eficiente que un listener por tarjeta
            const hoverVideos = this.grid.querySelectorAll('video.lazy-video-hover');
            if (hoverVideos.length > 0) {
                this.grid.addEventListener('mouseenter', (e) => {
                    const wrapper = e.target.closest('.showcase-card-wrapper');
                    if (!wrapper) return;
                    const video = wrapper.querySelector('video.lazy-video-hover');
                    if (!video) return;
                    if (video.readyState === 0) video.load();
                    video.play().catch(() => {});
                }, { passive: true, capture: true });

                this.grid.addEventListener('mouseleave', (e) => {
                    const wrapper = e.target.closest('.showcase-card-wrapper');
                    if (!wrapper) return;
                    const video = wrapper.querySelector('video.lazy-video-hover');
                    if (!video) return;
                    video.pause();
                    if (video.readyState >= 1) video.currentTime = 0;
                }, { passive: true, capture: true });
            }
        }

        // ── Dot Navigation ────────────────────────────────────────────

        setupDotNavigation() {
            if (this.dots.length === 0) return;
            this.dots.forEach(dot => {
                dot.addEventListener('click', () => {
                    const index = dot.dataset.index;
                    const targetCard = this.grid.querySelector(`.showcase-card-wrapper[data-index="${index}"]`);
                    if (targetCard) {
                        targetCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                    }
                });
            });
        }

        // Actualiza clases y atributos ARIA de los dots de forma sincronizada
        _updateDots(activeIndex) {
            this.dots.forEach(dot => {
                const isActive = parseInt(dot.dataset.index, 10) === activeIndex;
                dot.classList.toggle('active', isActive);
                dot.setAttribute('aria-current', isActive ? 'true' : 'false');
            });
        }

        // ── Mobile Layout ─────────────────────────────────────────────

        setupMobileLayout() {
            if (window.innerWidth >= DESKTOP_BP) return;

            // Swipe hint: se muestra al inicio, se oculta al primer scroll
            if (this.hint) {
                this.hint.classList.add('visible');
                const hideHint = () => {
                    this.hint.classList.remove('visible');
                    this.grid.removeEventListener('scroll', hideHint);
                };
                this.grid.addEventListener('scroll', hideHint, { passive: true });
            }

            // IntersectionObserver para estado activo de tarjeta y dots
            if (!('IntersectionObserver' in window)) return;

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const index = parseInt(entry.target.dataset.index, 10);

                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        entry.target.classList.add('active');
                        this.currentActiveIndex = index;
                        this._updateDots(index);

                        const video = entry.target.querySelector('video.lazy-video-hover');
                        if (video) video.play().catch(() => {});
                    } else if (!entry.isIntersecting) {
                        entry.target.classList.remove('active');
                        const video = entry.target.querySelector('video.lazy-video-hover');
                        if (video) { video.pause(); video.currentTime = 0; }
                    }
                });
            }, {
                root: this.grid,
                threshold: [0, 0.5],
                rootMargin: '0px'
            });

            this.cards.forEach(card => observer.observe(card));
            this.observers.push(observer);
        }

        // ── Desktop Effects ───────────────────────────────────────────

        setupDesktopEffects() {
            if (window.innerWidth < DESKTOP_BP) return;

            // Animación de entrada escalonada
            if ('IntersectionObserver' in window) {
                const entranceObserver = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const delay = parseInt(entry.target.dataset.index, 10) * 150;
                            setTimeout(() => entry.target.classList.add('visible'), delay);
                            entranceObserver.unobserve(entry.target);
                        }
                    });
                }, { threshold: 0.1 });
                this.cards.forEach(card => entranceObserver.observe(card));
                this.observers.push(entranceObserver);
            } else {
                this.cards.forEach(card => card.classList.add('visible'));
            }

            // 3D Tilt con ratón — RAF-throttled
            this.cards.forEach(wrapper => {
                if (wrapper.dataset.interaction !== 'tilt') return;
                const card = wrapper.querySelector('.showcase-card');
                if (!card) return;
                const gravityBtn = wrapper.querySelector('.btn-hover-gravity');
                const gravityContainer = wrapper.querySelector('.effect-gravity');
                let ticking = false;

                wrapper.addEventListener('mouseenter', () => {
                    card.style.willChange = 'transform';
                    if (gravityContainer) gravityContainer.style.willChange = 'transform';
                }, { passive: true });

                wrapper.addEventListener('mousemove', (e) => {
                    if (ticking) return;
                    ticking = true;
                    requestAnimationFrame(() => {
                        const rect = wrapper.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;
                        const centerX = rect.width / 2;
                        const centerY = rect.height / 2;
                        const rotateX = ((y - centerY) / centerY) * -5;
                        const rotateY = ((x - centerX) / centerX) * 5;
                        const gravity = parseFloat(wrapper.dataset.gravity) || 1.0;
                        const tiltX = ((x - centerX) / centerX) * 15 * gravity;
                        const tiltY = ((y - centerY) / centerY) * 15 * gravity;

                        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
                        card.style.setProperty('--tilt-x', `${tiltX}px`);
                        card.style.setProperty('--tilt-y', `${tiltY}px`);

                        if (gravityBtn) {
                            gravityBtn.style.setProperty('--btn-tilt-x', `${tiltX * 3}px`);
                            gravityBtn.style.setProperty('--btn-tilt-y', `${tiltY * 3}px`);
                        }
                        ticking = false;
                    });
                }, { passive: true });

                wrapper.addEventListener('mouseleave', () => {
                    card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
                    card.style.setProperty('--tilt-x', '0px');
                    card.style.setProperty('--tilt-y', '0px');
                    setTimeout(() => {
                        card.style.willChange = 'auto';
                        if (gravityContainer) gravityContainer.style.willChange = 'auto';
                    }, 400);
                    if (gravityBtn) {
                        gravityBtn.style.setProperty('--btn-tilt-x', '0px');
                        gravityBtn.style.setProperty('--btn-tilt-y', '0px');
                    }
                }, { passive: true });
            });

            // Gravity Buttons — movimiento propio con ratón
            const gravityBtns = this.grid.querySelectorAll('.btn-hover-gravity');
            gravityBtns.forEach(btn => {
                let btnTicking = false;
                btn.addEventListener('mousemove', (e) => {
                    if (btnTicking) return;
                    btnTicking = true;
                    requestAnimationFrame(() => {
                        const rect = btn.getBoundingClientRect();
                        const moveX = (e.clientX - rect.left - rect.width / 2) * 0.8;
                        const moveY = (e.clientY - rect.top - rect.height / 2) * 1.2;
                        btn.style.setProperty('--btn-tilt-x', `${moveX}px`);
                        btn.style.setProperty('--btn-tilt-y', `${moveY}px`);
                        btnTicking = false;
                    });
                }, { passive: true });

                btn.addEventListener('mouseleave', () => {
                    btn.style.setProperty('--btn-tilt-x', '0px');
                    btn.style.setProperty('--btn-tilt-y', '0px');
                }, { passive: true });
            });
        }

        // ── Gyroscope ─────────────────────────────────────────────────

        updateFromGyro(tiltX, tiltY) {
            if (!this.isVisible || window.innerWidth >= DESKTOP_BP) return;

            this.cards.forEach(wrapper => {
                if (!this.visibleCards.has(wrapper.dataset.index)) return;

                if (wrapper.dataset.interaction === 'tilt') {
                    const card = wrapper.querySelector('.showcase-card');
                    if (card) {
                        const rotateY = tiltX * 0.5;
                        const rotateX = -tiltY * 0.5;
                        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
                        const gravity = parseFloat(wrapper.dataset.gravity) || 1.0;
                        card.style.setProperty('--tilt-x', `${tiltX * gravity * 0.6}px`);
                        card.style.setProperty('--tilt-y', `${tiltY * gravity * 0.6}px`);
                    }
                }

                const btn = wrapper.querySelector('.btn-hover-gravity');
                if (btn) {
                    btn.style.setProperty('--btn-tilt-x', `${tiltX * 10}px`);
                    btn.style.setProperty('--btn-tilt-y', `${tiltY * 4}px`);
                }
            });
        }

        // ── Cleanup ───────────────────────────────────────────────────

        destroy() {
            this.observers.forEach(obs => obs.disconnect());
            this.observers = [];

            if (typeof Shopify !== 'undefined' && Shopify.designMode) {
                document.removeEventListener('shopify:section:unload', this._unloadHandler);
                document.removeEventListener('shopify:section:block:select', this._blockSelectHandler);
            }

            PuravidaShowcaseManager.instances = PuravidaShowcaseManager.instances.filter(i => i !== this);

            // Si no quedan instancias, desconectar el giroscopio
            if (PuravidaShowcaseManager.instances.length === 0) {
                PuravidaShowcaseManager.teardownGyroscope();
            }
        }
    }

    // =========================================
    // GLOBAL MANAGER — Singleton
    // Gestiona recursos compartidos (Giroscopio)
    // =========================================
    const PuravidaShowcaseManager = {
        instances: [],
        isListening: false,
        isPaused: true,
        lastUpdate: 0,
        updateInterval: 32, // ~30fps

        register(instance) {
            this.instances.push(instance);
            this.initGyroscope();
        },

        checkActiveInstances() {
            const hasVisible = this.instances.some(inst => inst.isVisible);
            if (hasVisible && this.isPaused) {
                this.isPaused = false;
            } else if (!hasVisible && !this.isPaused) {
                this.isPaused = true;
            }
        },

        initGyroscope() {
            if (this.isListening || window.innerWidth >= DESKTOP_BP || !window.DeviceOrientationEvent) return;

            this._orientationHandler = (event) => {
                if (this.isPaused) return;

                const now = performance.now();
                if (now - this.lastUpdate < this.updateInterval) return;
                this.lastUpdate = now;

                const beta = event.beta;
                const gamma = event.gamma;
                if (beta === null || gamma === null) return;

                const tiltX = Math.min(Math.max(gamma, -30), 30) * 0.5;
                const tiltY = Math.min(Math.max(beta - 45, -30), 30) * 0.5;

                requestAnimationFrame(() => {
                    this.instances.forEach(instance => {
                        if (instance.isVisible) instance.updateFromGyro(tiltX, tiltY);
                    });
                });
            };

            window.addEventListener('deviceorientation', this._orientationHandler, { passive: true });
            this.isListening = true;

            // iOS 13+ requiere permiso explícito del usuario
            if (typeof DeviceOrientationEvent.requestPermission === 'function') {
                const requestPermission = () => {
                    DeviceOrientationEvent.requestPermission().catch(() => {});
                    document.body.removeEventListener('click', requestPermission);
                    document.body.removeEventListener('touchstart', requestPermission);
                };
                document.body.addEventListener('click', requestPermission, { once: true });
                document.body.addEventListener('touchstart', requestPermission, { once: true, passive: true });
            }
        },

        teardownGyroscope() {
            if (!this.isListening || !this._orientationHandler) return;
            window.removeEventListener('deviceorientation', this._orientationHandler);
            this.isListening = false;
            this._orientationHandler = null;
        }
    };

    window.PuravidaShowcase = PuravidaShowcase;
    window.PuravidaShowcaseManager = PuravidaShowcaseManager;

})();
